import React, { useState, useRef } from 'react';
import { uploadFile } from '../../../services/api/formApi';

/**
 * File upload response component for the public form filler.
 *
 * Flow:
 * 1. User picks a file — we validate extension client-side against allowed types.
 * 2. We POST the file to /api/v1/p/files/upload?formId=X&questionId=Y.
 * 3. On success we get back a Cloudinary URL.
 * 4. We call onUpdate(question.id, url) so the URL becomes the answer for this question.
 */
const FileUploadResponse = ({ question, response, onUpdate, formId }) => {
    const { config } = question;
    const allowedTypes = config?.supportedFileTypes || [];
    const uploadedUrl = response?.value || null;

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [fileName, setFileName] = useState(null);
    const fileInputRef = useRef(null);

    const extensionToType = {
        pdf: 'PDF',
        docx: 'DOCX',
        doc: 'DOCX',
        jpeg: 'JPEG',
        jpg: 'JPEG',
        png: 'PNG',
        xlsx: 'XLSX',
        xls: 'XLSX',
        pptx: 'PPTX',
        ppt: 'PPTX',
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadError(null);

        // Client-side type check (mirrors server validation for instant feedback)
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        const resolvedType = extensionToType[ext];
        if (!resolvedType || !allowedTypes.includes(resolvedType)) {
            setUploadError(
                `"${file.name}" is not allowed. Accepted types: ${allowedTypes.join(', ')}.`
            );
            e.target.value = '';
            return;
        }

        setFileName(file.name);
        setUploading(true);
        onUpdate(question.id, null); // clear previous answer while uploading

        try {
            const { url } = await uploadFile(formId, question.id, file);
            onUpdate(question.id, url);
        } catch (err) {
            setUploadError(err.message || 'Upload failed. Please try again.');
            setFileName(null);
            onUpdate(question.id, null);
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = () => {
        setFileName(null);
        setUploadError(null);
        onUpdate(question.id, null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const acceptAttr = allowedTypes
        .map((t) => {
            const map = {
                PDF: '.pdf',
                DOCX: '.docx,.doc',
                JPEG: '.jpeg,.jpg',
                PNG: '.png',
                XLSX: '.xlsx,.xls',
                PPTX: '.pptx,.ppt',
            };
            return map[t] || '';
        })
        .join(',');

    return (
        <div>
            {/* Allowed types badges */}
            <div className="d-flex flex-wrap gap-1 mb-3">
                {allowedTypes.map((t) => (
                    <span
                        key={t}
                        className="badge bg-light text-secondary border"
                        style={{ fontSize: '0.75rem' }}
                    >
                        {t}
                    </span>
                ))}
            </div>

            {/* Upload area */}
            {!uploadedUrl && !uploading && (
                <label
                    className="d-flex flex-column align-items-center justify-content-center border border-2 border-dashed rounded-3 p-4 text-center"
                    style={{
                        cursor: 'pointer',
                        borderColor: uploadError ? '#dc3545' : '#6366f1',
                        background: uploadError ? '#fff5f5' : '#f8f7ff',
                        transition: 'all 0.2s',
                        minHeight: '130px',
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                        e.preventDefault();
                        const dt = e.dataTransfer;
                        if (dt.files?.length) {
                            fileInputRef.current.files = dt.files;
                            handleFileChange({ target: fileInputRef.current });
                        }
                    }}
                >
                    <svg
                        width="36"
                        height="36"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6366f1"
                        strokeWidth="1.5"
                        className="mb-2"
                    >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <span className="fw-medium text-dark" style={{ fontSize: '0.95rem' }}>
                        Click or drag & drop your file
                    </span>
                    <span className="text-secondary small mt-1">
                        Accepted: {allowedTypes.join(', ')}
                    </span>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={acceptAttr}
                        className="d-none"
                        onChange={handleFileChange}
                        id={`file-upload-${question.id}`}
                    />
                </label>
            )}

            {/* Uploading state */}
            {uploading && (
                <div
                    className="d-flex flex-column align-items-center justify-content-center border border-2 rounded-3 p-4"
                    style={{ minHeight: '130px', background: '#f8f7ff', borderColor: '#6366f1' }}
                >
                    <div
                        className="spinner-border mb-2"
                        role="status"
                        style={{ color: '#6366f1', width: '2rem', height: '2rem' }}
                    >
                        <span className="visually-hidden">Uploading...</span>
                    </div>
                    <span className="text-secondary small">Uploading {fileName}…</span>
                </div>
            )}

            {/* Uploaded successfully */}
            {uploadedUrl && !uploading && (
                <div
                    className="d-flex align-items-center gap-3 border rounded-3 p-3"
                    style={{ background: '#f0fdf4', borderColor: '#22c55e' }}
                >
                    <div
                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        style={{
                            width: '40px',
                            height: '40px',
                            background: '#dcfce7',
                        }}
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#16a34a"
                            strokeWidth="2"
                        >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                        </svg>
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                        <div className="fw-medium text-dark text-truncate" style={{ fontSize: '0.9rem' }}>
                            {fileName || 'File uploaded'}
                        </div>
                        <div className="text-success small">✓ Uploaded successfully</div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleRemove}
                        title="Remove file"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            )}

            {/* Error */}
            {uploadError && (
                <p className="text-danger small mt-2 mb-0">
                    <i className="fa fa-exclamation-circle me-1" aria-hidden="true" />
                    {uploadError}
                </p>
            )}
        </div>
    );
};

export default FileUploadResponse;
